import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  fetchBlofinLiveGuardRecovery,
  releaseBlofinLiveGuardRecovery,
} from '../../api/manualTrading';

export const DIAGNOSTIC_TTL_MS = 5 * 60 * 1000;

const STATUS_ACCENT = {
  WOULD_RELEASE: '#2f6f8f',
  RELEASED: '#2f7d55',
  STILL_OPEN: '#b26a00',
  EXPOSURE_UNAVAILABLE: '#a43e2a',
  OWNER_CHANGED: '#7a5c00',
  RELEASE_FAILED: '#a43e2a',
  RELEASE_NOT_CONFIRMED: '#a43e2a',
};

function formatCount(value) {
  return value === null || value === undefined ? '—' : value;
}

function formatContracts(value) {
  return value === null || value === undefined ? '—' : String(value);
}

function formatTimestamp(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'n/a';
  }
  return date.toLocaleString('fr-FR');
}

function maskIdentifier(value) {
  const text = value === null || value === undefined ? '' : String(value);
  if (!text) {
    return '—';
  }
  if (text.length <= 8) {
    return '••••';
  }
  return `${text.slice(0, 4)}…${text.slice(-4)}`;
}

function StatCard({ label, value, testId }) {
  return (
    <Box
      sx={{
        px: 1.6,
        py: 1.3,
        borderRadius: 3,
        background: '#fcf8ef',
        border: '1px solid rgba(23,50,60,0.10)',
        minWidth: 0,
      }}
    >
      <Typography variant="caption" sx={{ color: '#6b7c84', display: 'block', mb: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800 }} data-testid={testId}>
        {formatCount(value)}
      </Typography>
    </Box>
  );
}

function ReportSummary({ report, mode }) {
  const prefix = mode === 'result' ? 'guard-result' : 'guard-stat';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        gap: 1.2,
      }}
    >
      {mode === 'result' ? (
        <StatCard label="Libérés" value={report.released} testId={`${prefix}-released`} />
      ) : (
        <StatCard label="Verrous examinés" value={report.heldGuardsScanned} testId={`${prefix}-scanned`} />
      )}
      {mode === 'result' ? (
        <StatCard label="Candidats analysés" value={report.candidates} testId={`${prefix}-candidates`} />
      ) : (
        <StatCard label="Candidats" value={report.candidates} testId={`${prefix}-candidates`} />
      )}
      <StatCard
        label={mode === 'result' ? 'Encore libérables (théorique)' : 'Libérables'}
        value={report.wouldRelease}
        testId={`${prefix}-would-release`}
      />
      <StatCard label="Positions encore ouvertes" value={report.stillOpen} testId={`${prefix}-still-open`} />
      <StatCard label="Indisponibles" value={report.unavailable} testId={`${prefix}-unavailable`} />
      <StatCard label="Propriétaire changé" value={report.ownerChanged} testId={`${prefix}-owner-changed`} />
      <StatCard
        label="Libération non confirmée"
        value={report.releaseNotConfirmed}
        testId={`${prefix}-release-not-confirmed`}
      />
      <StatCard label="Échecs de libération" value={report.releaseFailed} testId={`${prefix}-release-failed`} />
    </Box>
  );
}

function EntryStatusChip({ status }) {
  const accent = STATUS_ACCENT[status] || '#4f646f';
  return (
    <Chip
      size="small"
      label={status || 'UNKNOWN'}
      sx={{ bgcolor: `${accent}1f`, color: accent, fontWeight: 800 }}
    />
  );
}

function ReportEntries({ entries }) {
  if (!entries || entries.length === 0) {
    return (
      <Alert severity="info">
        Aucune entrée pour ce rapport.
      </Alert>
    );
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Entrées des verrous historiques BloFin">
        <TableHead>
          <TableRow>
            <TableCell>Compte (masqué)</TableCell>
            <TableCell>Instrument</TableCell>
            <TableCell>Intention (masquée)</TableCell>
            <TableCell>État</TableCell>
            <TableCell>Détail</TableCell>
            <TableCell align="right">Contrats locaux</TableCell>
            <TableCell align="right">Contrats distants</TableCell>
            <TableCell>Libéré</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry, index) => (
            <TableRow key={`${entry.intentId || 'entry'}-${entry.status || 'status'}-${index}`}>
              <TableCell>{maskIdentifier(entry.accountIdentityRef)}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{entry.instrument || '—'}</TableCell>
              <TableCell>{maskIdentifier(entry.intentId)}</TableCell>
              <TableCell><EntryStatusChip status={entry.status} /></TableCell>
              <TableCell>{entry.detail || '—'}</TableCell>
              <TableCell align="right">{formatContracts(entry.remainingContracts)}</TableCell>
              <TableCell align="right">{formatContracts(entry.remoteContracts)}</TableCell>
              <TableCell>{entry.released ? 'Oui' : 'Non'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const BlofinLiveGuardRecoveryAdminPage = () => {
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticAt, setDiagnosticAt] = useState(null);
  const [postResult, setPostResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const requestInFlight = useRef(false);

  useEffect(() => {
    const interval = window.setInterval(() => setClock(Date.now()), 15000);
    return () => window.clearInterval(interval);
  }, []);

  const diagnosticFresh = Boolean(diagnostic && diagnosticAt)
    && (clock - diagnosticAt) <= DIAGNOSTIC_TTL_MS;

  const eligibleEntries = useMemo(
    () => (diagnostic?.entries || []).filter((entry) => entry.status === 'WOULD_RELEASE'),
    [diagnostic],
  );

  const handleAnalyze = useCallback(async () => {
    if (requestInFlight.current) {
      return;
    }
    requestInFlight.current = true;
    setBusy('analyze');
    setError('');
    setNotice('');
    try {
      const report = await fetchBlofinLiveGuardRecovery();
      setDiagnostic(report);
      setDiagnosticAt(Date.now());
      setClock(Date.now());
      setPostResult(null);
      setNotice('Analyse terminée en lecture seule. Aucun verrou n’a été libéré.');
    } catch (analyzeError) {
      setDiagnostic(null);
      setDiagnosticAt(null);
      setError(analyzeError?.message || 'Impossible d’analyser les verrous BloFin LIVE.');
    } finally {
      requestInFlight.current = false;
      setBusy(null);
    }
  }, []);

  const openConfirmation = () => {
    if (busy || !diagnosticFresh || eligibleEntries.length === 0) {
      return;
    }
    setAcknowledged(false);
    setConfirmOpen(true);
  };

  const closeConfirmation = () => {
    if (busy === 'release') {
      return;
    }
    setConfirmOpen(false);
    setAcknowledged(false);
  };

  const handleRelease = async () => {
    if (requestInFlight.current || !diagnosticFresh || !acknowledged) {
      return;
    }
    requestInFlight.current = true;
    setBusy('release');
    setError('');
    setNotice('');
    try {
      const report = await releaseBlofinLiveGuardRecovery();
      setPostResult(report);
      setDiagnostic(null);
      setDiagnosticAt(null);
      setConfirmOpen(false);
      setAcknowledged(false);
      setNotice('Résultat réel de la libération enregistré. Relancez une analyse pour vérifier l’état restant.');
    } catch (releaseError) {
      setConfirmOpen(false);
      setAcknowledged(false);
      setError(releaseError?.message || 'La libération des verrous a échoué.');
    } finally {
      requestInFlight.current = false;
      setBusy(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 840 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip label="ADMIN" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 800 }} />
                <Chip label="BloFin LIVE" sx={{ bgcolor: 'rgba(243,198,92,0.18)', color: '#f3c65c', fontWeight: 800 }} />
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Maintenance BloFin LIVE — Verrous historiques
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Analysez les verrous TP historiques encore détenus, puis libérez explicitement les
                candidats confirmés. L’analyse est strictement en lecture seule. La libération ne crée,
                ne ferme et ne modifie aucun ordre BloFin.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              <Button
                variant="outlined"
                onClick={handleAnalyze}
                disabled={Boolean(busy)}
                startIcon={busy === 'analyze' ? <CircularProgress size={18} color="inherit" /> : <RefreshRoundedIcon />}
                sx={{ borderColor: 'rgba(247,243,234,0.35)', color: '#f7f3ea', whiteSpace: 'nowrap' }}
              >
                {postResult ? 'Relancer l’analyse' : 'Analyser les verrous'}
              </Button>
              <Button
                variant="contained"
                onClick={openConfirmation}
                disabled={Boolean(busy) || !diagnosticFresh || eligibleEntries.length === 0}
                startIcon={<LockOpenRoundedIcon />}
                sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800, '&:hover': { bgcolor: '#e7b94f' } }}
              >
                Libérer les verrous éligibles
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {notice ? <Alert severity="success">{notice}</Alert> : null}

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 5, background: '#fff' }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                  Diagnostic des verrous encore détenus
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Le diagnostic est valable cinq minutes. Il ne constitue jamais une autorisation de
                  libération : le POST revérifie l’exposition distante de chaque candidat.
                </Typography>
              </Box>
              {diagnostic ? (
                <Chip
                  size="small"
                  color={diagnosticFresh ? 'success' : 'warning'}
                  label={diagnosticFresh
                    ? `Analyse du ${formatTimestamp(diagnosticAt)}`
                    : 'Analyse expirée — relancez l’analyse'}
                />
              ) : null}
            </Stack>

            {!diagnostic && !postResult ? (
              <Alert severity="info">
                Lancez une analyse pour afficher les verrous historiques. Aucune libération n’est
                déclenchée automatiquement.
              </Alert>
            ) : null}

            {diagnostic ? (
              <>
                <ReportSummary report={diagnostic} mode="diagnostic" />
                <Divider />
                <ReportEntries entries={diagnostic.entries} />
              </>
            ) : null}
          </Stack>
        </Paper>

        {postResult ? (
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 5, background: '#fff' }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                  Résultat réel de la libération
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Résultats partiels retournés par le backend. Aucun candidat n’est supposé libéré
                  sans confirmation explicite dans ce rapport.
                </Typography>
              </Box>
              <ReportSummary report={postResult} mode="result" />
              <Divider />
              <ReportEntries entries={postResult.entries} />
            </Stack>
          </Paper>
        ) : null}

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 5, background: '#fcf8ef', border: '1px solid rgba(23,50,60,0.08)' }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c' }}>
              Périmètre de sécurité LIVE
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Cette interface ne crée ni ne ferme de position, ne modifie ni n’annule aucun ordre
              BloFin, et ne touche ni aux historiques TP ni à <code>remaining_contracts</code>.
              Elle réutilise uniquement les endpoints ADMIN existants de diagnostic et de
              récupération des verrous.
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <Dialog open={confirmOpen} onClose={closeConfirmation} fullWidth maxWidth="md">
        <DialogTitle>Confirmation explicite — libération des verrous historiques</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Des ordres conditionnels SL/TP peuvent encore être présents sur BloFin. Cette opération
              ne les modifie ni ne les annule. Aucune position ne sera créée, fermée ou modifiée.
            </Alert>

            <Box>
              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                Candidats analysés
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c' }}>
                {formatCount(diagnostic?.candidates)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f', mt: 0.6 }}>
                Dont {eligibleEntries.length} éligible(s) à une tentative de libération.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 0.8 }}>
                Instruments éligibles prévus
              </Typography>
              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                {eligibleEntries.map((entry, index) => (
                  <Chip
                    key={`${entry.intentId || entry.instrument}-${index}`}
                    label={entry.instrument || '—'}
                    size="small"
                    sx={{ bgcolor: 'rgba(47,111,143,0.12)', color: '#2f6f8f', fontWeight: 700 }}
                  />
                ))}
              </Stack>
            </Box>

            <Alert severity="info">
              Le backend revérifiera l’exposition distante de chaque instrument au moment du POST.
              Le résultat peut donc différer du diagnostic. Consultez le rapport réel après l’action.
            </Alert>

            <FormControlLabel
              control={(
                <Checkbox
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  disabled={busy === 'release'}
                />
              )}
              label="Je comprends que des ordres conditionnels SL/TP peuvent subsister et que cette opération ne les modifie ni ne les annule."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation} disabled={busy === 'release'}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRelease}
            disabled={!acknowledged || busy === 'release'}
          >
            {busy === 'release' ? <CircularProgress size={18} color="inherit" /> : 'Confirmer la libération'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlofinLiveGuardRecoveryAdminPage;
